import { Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
	selector: 'ui-checkbox-dropdown',
	template:
		`<div class="d-inline-block" *ngIf="items.length" style="width: 100%; position: relative;">
			<button type="button" class="dropdown-toggle {{customClass ? customClass : 'custom-select'}}" data-toggle="dropdown" [ngClass]="classProp" (click)="isOpen = true" [disabled]="disabled">
				<div class="d-flex justify-content-between align-items-center">
					<span *ngIf="!customText" class="text-lines lines-2" uiEllipsisTooltip [innerHTML]="getSelectedItemNames()"></span>
					<span *ngIf="customText" class="text-lines lines-2" uiEllipsisTooltip>{{customText}}</span>
					<i *ngIf="customIcon" class="{{customIcon}} ml-1"></i>
				</div>
			</button>
			<div class="dropdown-menu bg-white border-primary type-dropdown-menu" style="min-width: 9rem; max-width: 20rem;">
				<ng-container *ngIf="isOpen">
					<div class="px-3 pt-1" (click)="$event.stopPropagation()">
						<div class="scrollable mb-2 ml-n2 pl-2" style="max-height: 11rem;">
							<ng-container *ngIf="(items | keywords:search:[showProp] | itemlist:filteredItems:'list') as visibleItems">
								<ng-container *ngFor="let type of visibleItems; let i = index">
									<div *ngIf="groupProp && isFirstInGroup(i)" class="mt-2">
										<hr *ngIf="i > 0" class="mt-0 mb-2">
										<div class="text-uppercase text-tiny text-secondary text-bold mb-1">{{groupLabel(type)}}</div>
									</div>
									<label class="custom-control custom-checkbox my-2 text-wrap">
										<input type="checkbox" class="custom-control-input"
											[checked]="isSelected(type)"
											(change)="toggleItemSelection(type)"
											[disabled]="disabled || isLocked(type)">
										<span class="custom-control-label text-normal text-no-transform text-dark text-base">
											{{type[showProp]}}
											<div *ngIf="descProp && type[descProp]" class="text-secondary text-small">{{type[descProp]}}</div>
										</span>
									</label>
								</ng-container>
								<p class="mx-0 my-2" *ngIf="!visibleItems.length">No items found.</p>
							</ng-container>
						</div>
					</div>
					<div class="px-3 pb-2">
						<div *ngIf="items?.length > 4" class="my-2">
							<input type="search" class="form-control"
								placeholder="Search"
								[(ngModel)]="search"
								autocomplete="chrome-off"
								spellcheck="false"
								appAutoFocus
								[disabled]="disabled">
						</div>
						<button *ngIf="filteredItems.list.length !== selectedItems?.length"
								type="button"
								class="btn btn-block btn-sm btn-primary"
								(click)="selectAllItems(); $event.stopPropagation()"
								[disabled]="disabled">Select All</button>
						<button *ngIf="selectedItems?.length > 0"
								type="button"
								class="btn btn-block btn-sm btn-secondary"
								(click)="unselectAllItems(); $event.stopPropagation()"
								[disabled]="disabled">Deselect All</button>
					</div>
				</ng-container>
			</div>
		</div>`
})
export class UICheckboxDropdownComponent {

	@Input() items = []; // this array of object should have the name property, or override the displayed property via showProp
	@Input() selectedItems = [];
	@Input() lockedItems = []; // items that render checked + disabled and cannot be toggled off
	@Input() dropdownName = '';
	@Input() disabled = false;
	@Input() showProp = 'name';
	@Input() selectProp = null;
	@Input() descProp: string = null;
	@Input() groupProp: string = null;
	@Input() groupLabels: { [k: string]: string } = null;
	@Input() classProp = '';
	@Input() maxShown = 2;
	@Input() customClass = '';
	@Input() customIcon = '';
	@Input() customText = '';
	@Output() selectionChange = new EventEmitter();

	isOpen = false;
	search = '';
	filteredItems = {
		list: []
	};

	isSelected(item) {
		return this.selectedItems?.some(selected => this.selectProp ? selected === item[this.selectProp] : selected[this.showProp] === item[this.showProp]);
	}

	isLocked(item) {
		return this.isLockedValue(this.selectProp ? item[this.selectProp] : item);
	}

	// Compares two selection values (ids when selectProp is set, whole items otherwise)
	isSameValue(a, b) {
		return this.selectProp ? a === b : a?.[this.showProp] === b?.[this.showProp];
	}

	isLockedValue(value) {
		return this.lockedItems?.some(locked => this.isSameValue(locked, value));
	}

	toggleItemSelection(item) {
		if (this.isLocked(item)) return; // locked items cannot be toggled off/on
		const index = this.selectedItems.findIndex(selected => this.selectProp ? selected === item[this.selectProp] : selected[this.showProp] === item[this.showProp]);
		if (index >= 0) {
			this.selectedItems.splice(index, 1);
		} else {
			this.selectedItems.push(this.selectProp ? item[this.selectProp] : item);
		}
		this.selectionChange.emit(this.selectedItems);
	}

	selectAllItems() {
		const selected = this.filteredItems.list.map(item => this.selectProp ? item[this.selectProp] : item);
		// Keep locked items selected even when the search filter hides them from the list
		(this.selectedItems || []).forEach(sel => {
			if (this.isLockedValue(sel) && !selected.some(s => this.isSameValue(s, sel))) selected.push(sel);
		});
		this.selectedItems = selected;
		this.selectionChange.emit(this.selectedItems);
	}

	unselectAllItems() {
		// Keep locked items selected — they cannot be unassigned
		this.selectedItems = (this.selectedItems || []).filter(sel => this.isLockedValue(sel));
		this.selectionChange.emit(this.selectedItems);
	}

	isFirstInGroup(index: number): boolean {
		if (!this.groupProp) return false;
		const list = this.filteredItems.list || [];
		if (index === 0) return true;
		return list[index - 1] && list[index][this.groupProp] !== list[index - 1][this.groupProp];
	}

	groupLabel(item): string {
		if (!this.groupProp) return '';
		const value = item[this.groupProp];
		if (this.groupLabels && this.groupLabels[value]) return this.groupLabels[value];
		return value;
	}

	getSelectedItemNames() {
		const selectedNames = this.items.filter(item => this.isSelected(item)).map(item => item[this.showProp]);
		let selectedString = this.dropdownName ? `${this.dropdownName}` : '';

		if (selectedString) {
			if (selectedNames.length) {
				selectedString += ': ';
			} else {
				selectedString = '<span class="text-secondary">' + selectedString + '</span>';
			}
		}

		if (selectedNames.length === (this.items || []).length) return `${selectedString}<span class="badge badge-secondary">All</span>`;
		if (selectedNames.length > this.maxShown) return `${selectedString}<span class="badge badge-secondary">${selectedNames.length}</span>`;
		return selectedNames.length ? `${selectedString}${selectedNames.join(', ')}` : selectedString;
	}

}
